/**
 * Reveal More Functionality
 *
 * Automatically truncates content at 300px height and provides accessible
 * expand functionality with proper ARIA attributes and keyboard support.
 * Prevents layout shift by starting truncated and expanding only if needed.
 *
 * Usage:
 * - Add class "js-reveal-more-element" to container
 * - Add class "js-reveal-more-btn" to button
 * - Optionally add class "js-reveal-more-content" to content wrapper
 *
 * HTML Structure:
 * <div class="js-reveal-more-element">
 *   <div class="js-reveal-more-content">Content to truncate</div>
 *   <button class="js-reveal-more-btn">Show more</button>
 * </div>
 */

(function () {
  "use strict";

  class RevealMore {
    constructor(element) {
      this.element = element;
      this.maxHeight = 350; // Truncation height in pixels
      this.isExpanded = false;
      this.init();
    }

    init() {
      // Find or create content wrapper
      this.content =
        this.element.querySelector(".js-reveal-more-content") ||
        this.createContentWrapper();
      this.button = this.element.querySelector(".js-reveal-more-btn");

      if (!this.button) { return; }

      // Set initial truncated state to prevent layout shift
      this.setInitialTruncatedState();

      this.setupAccessibility();

      // Check truncation after layout settles
      var checkFn = function () {
        this.checkTruncation();
      }.bind(this);

      requestAnimationFrame(checkFn);

      // Fallback if rAF doesn't fire (e.g., tab is hidden at load time)
      setTimeout(function () {
        if (this.element.dataset.checking === "true") {
          checkFn();
        }
      }.bind(this), 500);

      this.bindEvents();
    }

    /**
     * Creates content wrapper if none exists
     * Moves all children except button into wrapper
     */
    createContentWrapper() {
      const wrapper = document.createElement("div");
      wrapper.className = "js-reveal-more-content";

      [...this.element.children].forEach((child) => {
        if (!child.classList.contains("js-reveal-more-btn")) {
          wrapper.appendChild(child);
        }
      });

      this.element.insertBefore(wrapper, this.element.firstChild);
      return wrapper;
    }

    /**
     * Sets initial truncated state to prevent layout shift
     */
    setInitialTruncatedState() {
      this.element.dataset.truncated = "true";
      this.element.dataset.expanded = "false";
      this.element.dataset.checking = "true"; // Indicate we're still checking
      this.button.style.display = "none"; // Hide button initially
    }

    /**
     * Sets up ARIA attributes for accessibility
     * Links button to content with unique IDs
     */
    setupAccessibility() {
      const id = `reveal-content-${Math.random().toString(36).substr(2, 9)}`;
      this.button.setAttribute("aria-expanded", "false");
      this.button.setAttribute("aria-controls", id);
      this.content.setAttribute("id", id);
    }

    /**
     * Checks if content actually overflows maxHeight
     * If not, automatically reveals content without button
     */
    checkTruncation() {
      // Temporarily remove truncation to measure actual height
      this.element.dataset.checking = "true";
      this.element.dataset.truncated = "false";

      // Force layout recalculation
      const actualHeight = this.content.scrollHeight;

      if (actualHeight > this.maxHeight) {
        // Content overflows - keep truncated and show button
        // Remove inline style so CSS rules can apply
        this.button.style.display = "";
        this.element.dataset.truncated = "true";
        this.element.dataset.expanded = "false";
        this.element.dataset.checking = "false";

        // Trigger animation after a brief delay
        setTimeout(() => {
          this.button.style.animation = "showButton 0.3s ease-out forwards";
        }, 100);

        this.content.setAttribute(
          "aria-label",
          "Content is truncated. Use the show more button to reveal full content."
        );
      } else {
        // Content fits - auto-expand without button
        this.element.dataset.truncated = "false";
        this.element.dataset.expanded = "true";
        this.element.dataset.checking = "false";
        this.isExpanded = true;

        this.button.style.display = "none";
        this.button.setAttribute("aria-hidden", "true");
      }
    }

    /**
     * Binds click, keyboard, and resize events
     */
    bindEvents() {
      this.button.addEventListener("click", this.reveal.bind(this));
      this.button.addEventListener("keydown", (e) => {
        if (["Enter", " "].includes(e.key)) {
          e.preventDefault();
          this.reveal();
        }
      });

      // Re-check truncation on resize (debounced) - only if not manually expanded
      let resizeTimeout;
      window.addEventListener("resize", () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          if (!this.isExpanded) {
            this.setInitialTruncatedState();
            requestAnimationFrame(() => {
              this.checkTruncation();
            });
          }
        }, 250);
      });
    }

    /**
     * Reveals content permanently and hides button
     * Updates ARIA attributes and announces change
     */
    reveal() {
      // Add loading state
      this.element.dataset.loading = "true";

      this.isExpanded = true;
      this.element.dataset.expanded = "true";
      this.element.dataset.truncated = "false";
      this.button.setAttribute("aria-expanded", "true");

      // Remove loading state after a short delay
      setTimeout(() => {
        this.element.dataset.loading = "false";
      }, 100);

      this.announce("Content expanded and fully visible");

      // Hide button after content is fully visible
      this.button.style.display = "none";
      this.button.setAttribute("aria-hidden", "true");

      // Remove aria-label from content since it's no longer truncated
      setTimeout(() => {
        this.content.removeAttribute("aria-label");
      }, 500);
    }

    /**
     * Announces state changes to screen readers
     * Creates hidden live region if needed
     */
    announce(message) {
      let region = document.getElementById("reveal-announcements");
      if (!region) {
        region = Object.assign(document.createElement("div"), {
          id: "reveal-announcements",
          style:
            "position:absolute;left:-10000px;width:1px;height:1px;overflow:hidden",
        });
        region.setAttribute("aria-live", "polite");
        document.body.appendChild(region);
      }

      region.textContent = message;
      setTimeout(() => (region.textContent = ""), 1000);
    }
  }

  /**
   * Auto-initialize on DOM ready
   */
  const initRevealMore = () => {
    const elements = document.querySelectorAll(
      ".js-reveal-more-element:not([data-reveal-initialized])"
    );
    elements.forEach((el) => {
      el.dataset.revealInitialized = "true";
      new RevealMore(el);
    });
  };

  // Initialize when DOM is ready
  if (!document.revealMoreInitialized) {
    // If DOM already loaded, init immediately
    if (
      document.readyState === "interactive" ||
      document.readyState === "complete"
    ) {
      document.revealMoreInitialized = true;
      initRevealMore();
    } else {
      // Otherwise wait for DOMContentLoaded
      document.addEventListener("DOMContentLoaded", function () {
        if (!document.revealMoreInitialized) {
          document.revealMoreInitialized = true;
          initRevealMore();
        }
      });
    }

    // Window load as final safety net (e.g., deferred scripts or consent managers)
    window.addEventListener("load", function () {
      if (!document.revealMoreInitialized) {
        document.revealMoreInitialized = true;
        initRevealMore();
      }
    });
  }

  /**
   * Manual initialization for dynamic content
   *
   * @param {Element} container - Container to search within (defaults to document)
   *
   * Usage: window.initializeRevealMore(dynamicContentContainer);
   */
  window.initializeRevealMore = (container = document) => {
    container
      .querySelectorAll(
        ".js-reveal-more-element:not([data-reveal-initialized])"
      )
      .forEach((el) => {
        el.dataset.revealInitialized = "true";
        new RevealMore(el);
      });
  };
})(); // End IIFE
